import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import "./AddMedical.css";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DatePicker, { registerLocale } from "react-datepicker";
import { ro } from "date-fns/locale";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ro", ro);

const AddMedical = ({ isOpen, onClose, employeeData, onSuccess }) => {
  const [formData, setFormData] = useState({
    appointmentDate: "",
    hour: "",
    clinicId: "",
    alerts: "",
    expirationDate: "",
  });

  const [clinicsOptions, setClinicsOptions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [resultDoc, setResultDoc] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const appointmentRef = useRef(null);
  const expirationRef = useRef(null);
  const [errorToast, setErrorToast] = useState("");
  const formRef = useRef(null);
  const selectRef = useRef(null); // Ref pentru select

  // Stiluri custom pentru react-select - actualizat pentru a include bordură roșie
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      border: fieldErrors.clinicId ? '1px solid #d32f2f' : state.isFocused ? '1px solid #007BFF' : '1px solid #ccc',
      '&:hover': {
        border: fieldErrors.clinicId ? '1px solid #d32f2f' : state.isFocused ? '1px solid #007BFF' : '1px solid #888'
      },
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      minHeight: '38px',
      height: '38px',
      backgroundColor: state.isFocused ? '#fff' : '#fff',
      display: 'flex',
      alignItems: 'center',
      boxShadow: 'none',
      borderRadius: '4px',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    }),
    input: (base) => ({
      ...base,
      color: '#1a1a1a',
      margin: '0',
      padding: '0',
      '& input': {
        boxShadow: 'none !important',
        border: 'none !important',
        outline: 'none !important',
        padding: '0',
        margin: '0',
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999',
      fontSize: '14px',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1a1a1a',
      fontSize: '14px',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: '14px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#e6f2ff' : state.isFocused ? '#f0f0f0' : '#fff',
      color: state.isSelected ? '#006ce4' : '#1a1a1a',
      fontSize: '14px',
      '&:active': {
        backgroundColor: '#e6f2ff',
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
      padding: '4px 8px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '36px',
      display: 'flex',
      alignItems: 'center',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      marginTop: '8px',
      marginBottom: '8px',
    }),
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({
        appointmentDate: "",
        hour: "",
        clinicId: "",
        alerts: "",
        expirationDate: "",
      });
      setDocuments([]);
      setResultDoc([]);
      setFieldErrors({});
      
      axiosInstance.get("/suppliers/").then((res) => {
        const options = (res.data || []).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setClinicsOptions(options);
      });
    }
  }, [isOpen]);

  const handleSelectChange = (field) => (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const handleDocumentClick = (file) => {
    if (!file) return;

    const type = file.type?.toLowerCase() || "";
    const allowed = ["pdf", "jpeg", "jpg", "png"];
    const isAllowed = allowed.some(ext => type.includes(ext));

    if (!isAllowed) {
      setErrorToast("Preview available only for PDF and images.");
      setTimeout(() => setErrorToast(""), 3500);
      return;
    }

    const base = axiosInstance.defaults.baseURL?.replace(/\/$/, "") || "";

    if (file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, "_blank");
      return;
    }

    const url = typeof file === "string" ? file : file.url;
    let fullUrl = url;

    if (url.startsWith("/")) {
      fullUrl = `${base}${url}`;
    } else if (!url.startsWith("http")) {
      fullUrl = `${base}/media/${url}`;
    }

    window.open(fullUrl, "_blank");
  };

  const handleDateChange = (field, date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    const formatted = date.toLocaleDateString("en-CA");
    setFormData((prev) => ({ ...prev, [field]: formatted }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const { getRootProps: getRootPropsDocs, getInputProps: getInputPropsDocs } =
    useDropzone({
      accept: {
        "application/pdf": [],
        "image/jpeg": [],
        "image/png": [],
        "application/msword": [],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      },
      multiple: false,
      onDrop: (accepted) => {
        if (accepted.length > 0) {
          setDocuments(accepted);
          setFieldErrors((prev) => ({ ...prev, documents: "" }));
        }
      },
    });

  const { getRootProps: getRootPropsResult, getInputProps: getInputPropsResult } =
    useDropzone({
      accept: {
        "application/pdf": [],
        "image/jpeg": [],
        "image/png": [],
        "application/msword": [],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      },
      multiple: false,
      onDrop: (accepted) => {
        if (accepted.length > 0) setResultDoc(accepted);
      },
    });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));

    const input = e.target;
    if (value && value.trim() !== "") {
      input.classList.add("filled");
    } else {
      input.classList.remove("filled");
    }
  };

  const handleRemoveFile = (type) => {
    if (type === "documents") setDocuments([]);
    else setResultDoc([]);
  };

  const getPreviewIcon = (file) => {
    if (!file) return null;
    if (file.type.includes("pdf"))
      return <PictureAsPdfIcon style={{ fontSize: 32, color: "#d32f2f" }} />;
    if (file.type.includes("word"))
      return <DescriptionIcon style={{ fontSize: 32, color: "#1976d2" }} />;
    return <InsertDriveFileIcon style={{ fontSize: 32, color: "#757575" }} />;
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.appointmentDate)
      errors.appointmentDate = "Appointment date is required";
    if (!formData.hour) errors.hour = "Hour is required";
    if (!formData.clinicId) errors.clinicId = "Clinic is required";
    if (!formData.alerts.trim()) errors.alerts = "Alerts is required";
    if (documents.length === 0) errors.documents = "Document is required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) return;

    try {
      const payload = new FormData();

      payload.append("employee", employeeData?.id);
      payload.append("clinic", formData.clinicId);
      payload.append("date", formData.appointmentDate);
      payload.append("time", formData.hour + ":00");
      payload.append("alert", formData.alerts || 0);
      payload.append("status", "pending");

      if (formData.expirationDate)
        payload.append("expire_date", formData.expirationDate);

      if (documents.length > 0) payload.append("documents", documents[0]);
      if (resultDoc.length > 0) payload.append("result", resultDoc[0]);

      const res = await axiosInstance.post("medical-checkups/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 201 || res.status === 200) {
        onSuccess?.();
        setTimeout(() => onClose(), 300);
      }
    } catch (err) {
      console.error("❌ Error creating record:", err);
      alert("❌ Failed to add medical record. Try again later.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="addmedical-page">
      <div className="medical-overlay">
        {errorToast && (
          <div className="error-snackbar">{errorToast}</div>
        )}

        <div className="medical-modal">
          <div className="medical-header">
            <h2>Create Medical Record</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <hr className="header-divider" />

          <form ref={formRef} onSubmit={handleFormSubmit} className="medical-form" noValidate>
            {/* ROW 1 — Data + Ora */}
            <div className="row">
              <div className="field">
                <label>
                  Appointment date <span className="required">*</span>
                </label>

                <div className="datepicker-container">
                  <DatePicker
                    ref={appointmentRef}
                    selected={formData.appointmentDate ? new Date(formData.appointmentDate) : null}
                    onChange={(date) => handleDateChange("appointmentDate", date)}
                    dateFormat="dd.MM.yyyy"
                    locale="ro"
                    className={`datepicker-input-with-icon ${fieldErrors.appointmentDate ? "field-error-border" : ""}`}
                    placeholderText="dd.mm.yyyy"
                    popperPlacement="bottom-start"
                    required
                  />

                  <span
                    className="calendar-icon-inside"
                    onClick={() => appointmentRef.current?.setOpen(true)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                </div>

                {fieldErrors.appointmentDate && (
                  <span className="field-error">{fieldErrors.appointmentDate}</span>
                )}
              </div>

              {/* ORA */}
              <div className="field">
                <label>
                  Hour <span className="required">*</span>
                </label>

                <div className="datepicker-container">
                  <DatePicker
                    ref={(r) => (window.hourPickerRef = r)}
                    selected={formData.hour ? new Date(`1970-01-01T${formData.hour}`) : null}
                    onChange={(date) => {
                      if (date) {
                        const hours = date.getHours().toString().padStart(2, "0");
                        const minutes = date.getMinutes().toString().padStart(2, "0");
                        setFormData((prev) => ({ ...prev, hour: `${hours}:${minutes}` }));
                        setFieldErrors((prev) => ({ ...prev, hour: "" }));
                      } else {
                        setFormData((prev) => ({ ...prev, hour: "" }));
                      }
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={1}
                    timeCaption="Ora"
                    timeFormat="HH:mm"
                    dateFormat="HH:mm"
                    locale="ro"
                    placeholderText="hh:mm"
                    className={`datepicker-input-with-icon ${fieldErrors.hour ? "field-error-border" : ""}`}
                    popperPlacement="bottom-start"
                    required
                  />

                  <span
                    className="calendar-icon-inside"
                    onClick={() => window.hourPickerRef?.setOpen(true)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </span>
                </div>

                {fieldErrors.hour && (
                  <span className="field-error">{fieldErrors.hour}</span>
                )}
              </div>
            </div>

            {/* CLINICĂ */}
            <div className="field">
              <label>
                Clinic <span className="required">*</span>
              </label>

              <div className={`select-wrapper ${fieldErrors.clinicId ? "field-error-border" : ""}`}>
                <Select
                  ref={selectRef}
                  value={clinicsOptions.find(option => option.value === formData.clinicId)}
                  onChange={handleSelectChange("clinicId")}
                  options={clinicsOptions}
                  placeholder="Select clinic"
                  className="multiselect-field"
                  classNamePrefix="select"
                  isSearchable={true}
                  isClearable={true}
                  styles={customSelectStyles}
                  required={false}
                  onBlur={() => {
                    // Adaugă o mică întârziere pentru a asigura validarea corectă
                    setTimeout(() => {
                      if (!formData.clinicId && fieldErrors.clinicId) {
                        setFieldErrors(prev => ({ ...prev, clinicId: "Clinic is required" }));
                      }
                    }, 100);
                  }}
                />
              </div>

              {fieldErrors.clinicId && (
                <span className="field-error">{fieldErrors.clinicId}</span>
              )}
            </div>

            {/* ALERT DAYS */}
            <div className="field">
              <label>
                Alerts <span className="required">*</span>
              </label>

              <input
                type="text"
                name="alerts"
                value={formData.alerts}
                placeholder="Days before expiration"
                onChange={handleInputChange}
                className={fieldErrors.alerts ? "field-error-border" : ""}
                required
              />

              {fieldErrors.alerts && (
                <span className="field-error">{fieldErrors.alerts}</span>
              )}
            </div>

            {/* DOCUMENT UPLOAD */}
            <div className="field">
              <label>
                Documents <span className="required">*</span>
              </label>

              <div
                {...getRootPropsDocs({
                  className: `dropzone-medical ${fieldErrors.documents ? "field-error-border" : ""}`,
                })}
              >
                <input {...getInputPropsDocs()} />
                <p>Drag & drop or click to select file</p>
                <CloudUploadIcon style={{ fontSize: 26, color: "#888" }} />
              </div>

              {documents.length > 0 && (
                <div className="file-preview">
                  <div
                    className="file-preview-clickable"
                    onClick={() => handleDocumentClick(documents[0])}
                    title="Click to open document"
                  >
                    {getPreviewIcon(documents[0])}
                    <span className="file-name-link">{documents[0].name}</span>
                  </div>

                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={() => handleRemoveFile("documents")}
                  >
                    <DeleteIcon fontSize="small" />
                  </button>
                </div>
              )}

              {fieldErrors.documents && (
                <span className="field-error">{fieldErrors.documents}</span>
              )}
            </div>

            {/* RESULT DOCUMENT */}
            <div className="field">
              <label>Result document</label>

              <div {...getRootPropsResult({ className: "dropzone-medical" })}>
                <input {...getInputPropsResult()} />
                <p>Drag & drop or click to select file</p>
                <CloudUploadIcon style={{ fontSize: 26, color: "#888" }} />
              </div>

              {resultDoc.length > 0 && (
                <div className="file-preview">
                  <div
                    className="file-preview-clickable"
                    onClick={() => handleDocumentClick(resultDoc[0])}
                    title="Click to open result document"
                  >
                    {getPreviewIcon(resultDoc[0])}
                    <span className="file-name-link">{resultDoc[0].name}</span>
                  </div>

                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={() => handleRemoveFile("result")}
                  >
                    <DeleteIcon fontSize="small" />
                  </button>
                </div>
              )}
            </div>

            {/* EXPIRATION DATE */}
            <div className="field">
              <label>Expiration date</label>

              <div className="datepicker-container">
                <DatePicker
                  ref={expirationRef}
                  selected={
                    formData.expirationDate ? new Date(formData.expirationDate) : null
                  }
                  onChange={(date) => handleDateChange("expirationDate", date)}
                  dateFormat="dd.MM.yyyy"
                  locale="ro"
                  className={`datepicker-input-with-icon ${fieldErrors.expirationDate ? "field-error-border" : ""}`}
                  placeholderText="dd.mm.yyyy"
                  popperPlacement="bottom-start"
                />

                <span
                  className="calendar-icon-inside"
                  onClick={() => expirationRef.current?.setOpen(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="actions">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="done-btn">
                Save
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AddMedical;